import { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';

/* Suche oben rechts auf /coach (Mockup „Nobiro Coach“, Betreiber 30.10.2026): „Kunde oder Seite suchen … ⌘K“. Seiten aus der
   Seitenleiste, Kunden aus dem Portfolio (`GET /api/coach/portfolio` — dieselbe Sichtregel wie überall auf /coach). Die
   Kundenliste wird erst beim ersten Öffnen geladen und dann behalten. */

const norm = (s) => (s || '').toLocaleLowerCase('de-DE').normalize('NFD').replace(/[̀-ͯ]/g, '');

export default function CoachSuche({ coach, bereiche, gehe }) {
  const [q, setQ] = useState('');
  const [offen, setOffen] = useState(false);
  const [kunden, setKunden] = useState(null);
  const [markiert, setMarkiert] = useState(0);
  const feld = useRef(null);
  const geladen = useRef(false);

  useEffect(() => {
    const taste = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); feld.current?.focus(); setOffen(true); }
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, []);

  useEffect(() => {
    if (!offen || geladen.current) return;
    geladen.current = true;
    coach.get('/portfolio', { params: { zeitraum: '30' } })
      .then((r) => setKunden((r.data?.kunden || []).map((z) => ({ id: z.lead_id, name: z.name }))))
      .catch(() => setKunden([]));
  }, [offen, coach]);

  const treffer = useMemo(() => {
    const n = norm(q.trim());
    if (!n) return [];
    const seiten = bereiche.filter((b) => norm(b.label).includes(n) || norm(b.titel).includes(n))
      .slice(0, 5).map((b) => ({ art: 'seite', key: `s-${b.id}`, text: b.label, zusatz: b.gruppe, ziel: { ansicht: b.id } }));
    const leute = (kunden || []).filter((k) => norm(k.name).includes(n))
      .slice(0, 8).map((k) => ({ art: 'kunde', key: `k-${k.id}`, text: k.name, zusatz: 'Kundenakte', ziel: { ansicht: 'kunde', kunde: k.id } }));
    return [...leute, ...seiten];
  }, [q, bereiche, kunden]);

  useEffect(() => { setMarkiert(0); }, [q]);

  const waehle = (t) => { setQ(''); setOffen(false); feld.current?.blur(); gehe(t.ziel); };
  const tasten = (e) => {
    if (e.key === 'Escape') { setOffen(false); feld.current?.blur(); return; }
    if (!treffer.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setMarkiert((m) => (m + 1) % treffer.length); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setMarkiert((m) => (m - 1 + treffer.length) % treffer.length); }
    if (e.key === 'Enter') { e.preventDefault(); waehle(treffer[markiert]); }
  };

  return (
    <div className="relative w-full sm:w-[300px]">
      <label className="flex h-11 items-center gap-2.5 rounded-[10px] border border-border bg-card px-3">
        <span className="sr-only">Kunde oder Seite suchen</span>
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <input ref={feld} value={q} onChange={(e) => { setQ(e.target.value); setOffen(true); }} onFocus={() => setOffen(true)}
          onBlur={() => setTimeout(() => setOffen(false), 150)} onKeyDown={tasten} placeholder="Kunde oder Seite suchen …"
          role="combobox" aria-expanded={offen && treffer.length > 0} aria-controls="coach-suche-liste" aria-autocomplete="list"
          data-testid="coach-globalsuche"
          className="min-w-0 flex-1 bg-transparent text-sm font-medium placeholder:text-muted-foreground focus:outline-none" />
        <kbd className="hidden text-[13px] font-medium text-muted-foreground sm:inline">⌘K</kbd>
      </label>
      {offen && q.trim() && (
        <ul id="coach-suche-liste" role="listbox" className="absolute right-0 z-40 mt-1.5 max-h-80 w-full overflow-y-auto rounded-[12px] border border-border bg-popover p-1.5 shadow-xl">
          {kunden === null && <li className="px-3 py-2 text-sm text-muted-foreground">Kunden werden geladen …</li>}
          {kunden !== null && !treffer.length && <li className="px-3 py-2 text-sm text-muted-foreground">Nichts gefunden</li>}
          {treffer.map((t, i) => (
            <li key={t.key} role="option" aria-selected={i === markiert}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => waehle(t)} onMouseEnter={() => setMarkiert(i)}
                className={`flex w-full items-center justify-between gap-3 rounded-[8px] px-3 py-2 text-left text-sm ${i === markiert ? 'bg-muted' : ''}`}>
                <span className="truncate font-semibold">{t.text}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{t.zusatz}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
